import { useEffect, useRef, useState, type RefObject } from 'react';
import type { RoadmapRenderer } from './roadmapRenderer';

/**
 * The scene's canvas is mounted inside the scrolled world so it scrolls with the HTML
 * roadmap, which owns layout, focus and all actions.
 */
export function RoadmapScene({ viewportRef }: { viewportRef: RefObject<HTMLDivElement | null> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    const world = canvas?.closest<HTMLElement>('.rm-world');
    if (!viewport || !canvas || !world) return;
    let stopped = false;
    let renderer: RoadmapRenderer | undefined;
    const abort = new AbortController();
    const fallback = () => {
      viewport.classList.remove('has-live-3d');
      if (!stopped) setStatus('fallback');
      renderer?.dispose();
    };
    const timeout = window.setTimeout(() => {
      abort.abort();
      fallback();
    }, 20000);

    // Loading Three.js and the model is deferred until this roadmap is on screen.
    void import('./roadmapRenderer')
      .then(async ({ createRoadmapRenderer }) => {
        if (stopped || abort.signal.aborted) return;
        renderer = await createRoadmapRenderer(canvas, world, abort.signal, fallback);
        if (stopped || abort.signal.aborted) {
          renderer.dispose();
          return;
        }
        viewport.classList.add('has-live-3d');
        renderer.invalidate();
        setStatus('ready');
      })
      .catch(() => fallback())
      .finally(() => window.clearTimeout(timeout));

    return () => {
      stopped = true;
      abort.abort();
      window.clearTimeout(timeout);
      viewport.classList.remove('has-live-3d');
      renderer?.dispose();
    };
  }, [viewportRef]);

  return (
    <div className="rm-3d-layer" data-testid="roadmap-3d" data-status={status} aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}
